import { useId, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { parseCartCsv } from './cart-csv'
import type { CartCsvRow } from './cart-csv'

export interface CopyFromExcelModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** Called with the parsed rows + any per-row parse errors once the buyer
   * submits the pasted block. Resolving SKUs against the catalog and
   * POSTing lines is the caller's job (CartPage), same division as
   * `CsvImportControl` — this modal only owns the paste + parse step. */
  readonly onParsed: (result: { rows: readonly CartCsvRow[]; errors: readonly string[] }) => void
}

/**
 * "Copy from Excel" (Cart redesign header action / empty-state tile): paste
 * a `Part # / Qty` block copied straight out of a spreadsheet — tab-
 * delimited, the same shape `cart-csv.ts` already parses for a `.csv`
 * upload (its delimiter sniffing accepts either). Shares that parser with
 * `CsvImportControl` rather than inventing a second bulk-add code path.
 */
export function CopyFromExcelModal({ open, onClose, onParsed }: CopyFromExcelModalProps) {
  const [pasted, setPasted] = useState('')
  const textareaId = useId()

  function handleSubmit() {
    onParsed(parseCartCsv(pasted))
    setPasted('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        setPasted('')
        onClose()
      }}
      title="Copy from Excel"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={pasted.trim() === ''} onClick={handleSubmit}>
            Add to cart
          </Button>
        </>
      }
    >
      <div className="form-field">
        <label htmlFor={textareaId}>Part # / Qty</label>
        <textarea
          id={textareaId}
          className="copy-from-excel-textarea"
          rows={8}
          value={pasted}
          onChange={(event) => setPasted(event.target.value)}
          placeholder={'B6800ZZ\t8\nUCP204\t2'}
        />
        <p className="caption-text">
          Paste a two-column block straight out of a spreadsheet (Part # in one column, Qty in the
          next) — we add every row we recognize.
        </p>
      </div>
    </Modal>
  )
}
